import { useCallback, useId } from "react"
import { Alert, Box, Button, CircularProgress, List, ListItemButton, ListItemText, ListSubheader, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { useScheduledWorkflowDeliverables } from "resources/scheduled-workflows/hooks"
import { SUBHEADER_SX } from "../chatbox/side-panel"
import { reportEntryLabel } from "./report-format"
import { viewerTimeZone } from "./schedule-format"

const MESSAGE_SX = { m: 1 } satisfies SxProps<Theme>
const EMPTY_SX = { px: 2, py: 1, color: "text.secondary", typography: "body2" } satisfies SxProps<Theme>
const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>
const SHOW_MORE_SX = { justifyContent: "flex-start", textTransform: "none", mx: 1 } satisfies SxProps<Theme>

const VIEWER_ZONE = viewerTimeZone()

export function ReportsSection({ onOpen }: { onOpen: (deliverableId: string) => void }) {
  const headingId = useId()
  const { data, isLoading, isError, isFetchNextPageError, hasNextPage, isFetchingNextPage, fetchNextPage } = useScheduledWorkflowDeliverables()
  const deliverables = data?.pages.flatMap((page) => page.deliverables) ?? []
  const showMore = useCallback(() => { void fetchNextPage() }, [fetchNextPage])

  return (
    <Box component="section" aria-labelledby={headingId} data-testid="chatbox-reports-section">
      <ListSubheader id={headingId} component="div" disableSticky sx={SUBHEADER_SX}>Reports</ListSubheader>
      {isError && data == null && <Alert severity="error" sx={MESSAGE_SX}>Couldn&apos;t load your reports. Please try again later.</Alert>}
      {isLoading && (
        <Box sx={LOADING_SX}>
          <CircularProgress size={20} aria-label="Loading reports" />
        </Box>
      )}
      {data != null && deliverables.length === 0 && <Typography sx={EMPTY_SX}>Reports land here once a workflow runs.</Typography>}
      <List dense disablePadding>
        {deliverables.map((deliverable) => (
          <ListItemButton key={deliverable.deliverableId} onClick={(event) => { event.currentTarget.focus(); onOpen(deliverable.deliverableId) }}>
            <ListItemText primary={reportEntryLabel(deliverable, VIEWER_ZONE)} />
          </ListItemButton>
        ))}
      </List>
      {isFetchNextPageError && (
        <Alert severity="error" sx={MESSAGE_SX} action={<Button size="small" onClick={showMore}>Try again</Button>}>Couldn&apos;t load more reports.</Alert>
      )}
      {hasNextPage && !isFetchNextPageError && (
        <Button variant="text" size="small" sx={SHOW_MORE_SX} disabled={isFetchingNextPage} onClick={showMore}>
          Show more
        </Button>
      )}
    </Box>
  )
}
